import { boxToRect, getBoundsOfBoxes, rectToBox } from '../geometry';
import type { Dimensions, Rect, Viewport } from '../types';

/** What the minimap draws: its SVG viewBox, the visible area inside it, and the margin, all in flow units. */
export type MinimapView = { viewBox: Rect; visibleRect: Rect; margin: number };

/** Margin around the content, in minimap pixels (v11 look). */
const MARGIN_PX = 5;
const HALF = 0.5;

/** The part of the flow the main canvas currently shows. */
export const getVisibleRect = ({ x, y, zoom }: Viewport, { width, height }: Dimensions): Rect => ({
  // `0 - x` rather than `-x`: an unpanned view gives 0, not -0.
  x: (0 - x) / zoom,
  y: (0 - y) / zoom,
  width: width / zoom,
  height: height / zoom,
});

/**
 * Fits the nodes and the visible area into a minimap of `size`, keeping its aspect ratio and centring the content
 * with a small margin. `nodesRect` is null when there are no measured nodes.
 */
export const getMinimapView = (nodesRect: Rect | null, viewport: Viewport, paneSize: Dimensions, size: Dimensions): MinimapView => {
  const visibleRect = getVisibleRect(viewport, paneSize);
  const content = nodesRect ? boxToRect(getBoundsOfBoxes(rectToBox(nodesRect), rectToBox(visibleRect))) : visibleRect;
  const scale = Math.max(content.width / size.width, content.height / size.height);
  const width = scale * size.width;
  const height = scale * size.height;
  const margin = MARGIN_PX * scale;
  return {
    viewBox: {
      x: content.x - (width - content.width) * HALF - margin,
      y: content.y - (height - content.height) * HALF - margin,
      width: width + margin * 2,
      height: height + margin * 2,
    },
    visibleRect,
    margin,
  };
};
